import { pointerWithin } from '@dnd-kit/core'
import classNames from 'classnames'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../constants/common'
import { DragDropProvider } from '../DragDrop'

import { CalendarGridContext } from './CalendarGrid.context'
import { useCalendarGridDragDrop } from './CalendarGrid.hooks'
import {
  getIsCalendarGridBodyMessageShown,
  getIsCalendarGridItemsHidden,
} from './CalendarGrid.utils'
import { CalendarGridAgenda } from './CalendarGridAgenda'
import { CalendarGridBodyMessage } from './CalendarGridBodyMessage'
import { CalendarGridChip } from './CalendarGridChip'
import { CalendarGridMonth } from './CalendarGridMonth'
import { CalendarGridNav } from './CalendarGridNav'
import { CalendarGridWeek } from './CalendarGridWeek'

import * as styles from './CalendarGrid.module.css'

function CalendarGrid({
  DayPopoverActions,
  ItemCard,
  ItemRow,
  ItemStatus,
  ItemTag,
  bodyMessage,
  calendar: {
    anchor,
    dayLoads,
    days,
    handleChangeMode,
    handleGoToNext,
    handleGoToPrev,
    handleGoToToday,
    handleReload,
    isAgendaMode,
    isMonthMode,
    isRangeLoading,
    isTodayVisible,
    items,
    locale,
    mode,
    modes,
    now,
    serverError,
    today,
    weekdays,
  } = EMPTY_OBJECT,
  hasTimedItems,
  isLoading,
  notice,
  onClickEmptyCell,
  onClickItem,
  onClickSecondaryLabel,
  onMoveItem,
  onMoveItemTime,
  onSelectEmptyDays,
  onSelectEmptyRange,
}) {
  const { handleDragCancel, handleDragEnd, handleDragMove, previewItem } =
    useCalendarGridDragDrop({ onMoveItem, onMoveItemTime })

  const isItemsHidden = getIsCalendarGridItemsHidden({
    isRangeLoading,
    serverError,
  })

  const shownItems = isItemsHidden ? EMPTY_LIST : items

  const shownDayLoads = isItemsHidden ? EMPTY_OBJECT : dayLoads

  function renderDraggedChip(activeId, activeData) {
    return activeData?.item ? <CalendarGridChip item={activeData.item} /> : null
  }

  return (
    <CalendarGridContext
      value={{
        DayPopoverActions,
        ItemCard,
        ItemRow,
        ItemStatus,
        ItemTag,
        isLoading,
        loadError: serverError,
        onReload: handleReload,
      }}
    >
      <div className={styles.CalendarGrid}>
        <CalendarGridNav
          anchor={anchor}
          days={days}
          isAgendaMode={isAgendaMode}
          isTodayVisible={isTodayVisible}
          locale={locale}
          mode={mode}
          modes={modes}
          onChangeMode={handleChangeMode}
          onGoToNext={handleGoToNext}
          onGoToPrev={handleGoToPrev}
          onGoToToday={handleGoToToday}
        />
        {notice}
        <div className={styles.body}>
          <DragDropProvider
            className={classNames(styles.board, {
              [styles.loading]: isLoading,
              [styles.refetching]: isLoading && !isRangeLoading,
            })}
            collisionDetection={pointerWithin}
            dndContextProps={{ onDragMove: handleDragMove }}
            renderOverlay={renderDraggedChip}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragMove}
          >
            {isAgendaMode ? (
              <CalendarGridAgenda
                days={days}
                isLoading={isLoading}
                items={shownItems}
                message={bodyMessage}
                today={today}
                onClickItem={onClickItem}
                onClickSecondaryLabel={onClickSecondaryLabel}
              />
            ) : isMonthMode ? (
              <CalendarGridMonth
                canMoveItems={Boolean(onMoveItem)}
                dayLoads={shownDayLoads}
                days={days}
                isSkeleton={isRangeLoading}
                items={shownItems}
                today={today}
                weekdays={weekdays}
                onClickItem={onClickItem}
                onClickSecondaryLabel={onClickSecondaryLabel}
              />
            ) : (
              <CalendarGridWeek
                canMoveItems={Boolean(onMoveItem)}
                canMoveItemsInTime={Boolean(onMoveItemTime)}
                dayLoads={shownDayLoads}
                days={days}
                hasTimedItems={hasTimedItems}
                isSkeleton={isRangeLoading}
                items={shownItems}
                now={now}
                previewItem={previewItem}
                today={today}
                onClickEmptyCell={onClickEmptyCell}
                onClickItem={onClickItem}
                onClickSecondaryLabel={onClickSecondaryLabel}
                onSelectEmptyDays={onSelectEmptyDays}
                onSelectEmptyRange={onSelectEmptyRange}
              />
            )}
          </DragDropProvider>
          {getIsCalendarGridBodyMessageShown({
            bodyMessage,
            isAgendaMode,
            isRangeLoading,
          }) ? (
            <CalendarGridBodyMessage
              {...bodyMessage}
              className={classNames(styles.bodyMessage, {
                [styles.bodyMessageTop]: !isMonthMode,
              })}
            />
          ) : null}
        </div>
      </div>
    </CalendarGridContext>
  )
}

export default CalendarGrid
